import React, { useCallback, useState } from "react";
import { SessionEventObservable } from "@/store/sessions/SessionEventObservable";
import { mdsColors } from "@/design-system/foundations";
import { MdsIcon, MdsIconKind } from "@/design-system/components/icon";
import { css } from "@/domains/emotion";
import { observer } from "mobx-react-lite";
import { SessionStatus } from "@/store/sessions/types";
import { SessionStatusIcon } from "@/components/active-session/session/session-status-icon";
import { useAppStore } from "@/store";
import { canRenderForm, InputForm } from "@/components/active-session/input-forms";
import {
  getNextInputRequestValue,
  groupUpMessageEventsByType,
} from "@/components/active-session/helpers/input-request-helpers";
import { FadeInAnimator } from "@/design-system/components/animation";
import { MDSIconButton } from "@/mds";
import {
  Wrapper,
  SessionTitle,
  TitleAndStatusContainer,
  SessionBody,
  MessagesContainer,
  ReasoningGroup,
  ReasoningHeader,
  Icon,
  DebugContent,
  DebugSessionId,
  DismissLink,
} from "@/components/active-session/session/session.styles";
import { SessionEvent } from "@/components/active-session/session/session-event";

interface SessionProps {
  title: string;
  sessionId: string;
  status: SessionStatus;
  events: SessionEventObservable[];
  onDismiss?: () => void;
}

/**
 * @deprecated - Use the newer MDS components instead.
 *
 * - If something doesn't exist yet, please create a new MDS component and
 *   have it reviewed by the team!
 *
 * - If something needs to hook into our `stores`, add it to the /components directory.
 *
 * - If something is a one-off; please colocate it with the component/page that
 *   needs it.
 *
 * - If you have questions, please ask in the #hub-eng-web-team Slack channel.
 */
export const Session: React.FC<SessionProps> = observer(
  ({ title, status, events, onDismiss, sessionId }) => {
    const { store } = useAppStore();
    const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});
    const [isCollapsed, setIsCollapsed] = useState(false);
    const orderedMessageEvents = events
      .filter(event => event.modelData.kind === "MESSAGE_SENT")
      .toSorted((a, b) => a.locallyCreatedAt.getTime() - b.locallyCreatedAt.getTime());

    const orderedInputEvents = events
      .filter(
        event =>
          event.modelData.kind === "INPUT_REQUESTED" || event.modelData.kind === "INPUT_PROVIDED"
      )
      .toSorted((a, b) => a.locallyCreatedAt.getTime() - b.locallyCreatedAt.getTime());

    const nextInputRequestValue = getNextInputRequestValue(orderedInputEvents);
    const groupedMessages = groupUpMessageEventsByType(orderedMessageEvents);

    // Toggle the collapsed state of a specific reasoning group
    const toggleReasoningGroup = (groupIndex: number) => {
      setCollapsedGroups(prev => ({
        ...prev,
        [groupIndex]: !prev[groupIndex],
      }));
    };

    const handleDismissAndCancel = useCallback(() => {
      store.sessions.cancelImportSession(sessionId);
      onDismiss?.();
    }, [sessionId, store.sessions, onDismiss]);

    return (
      <Wrapper>
        <SessionTitle
          $isCollapsed={isCollapsed}
          onClick={() => {
            if (!isCollapsed) return;
            setIsCollapsed(!isCollapsed);
          }}
        >
          <TitleAndStatusContainer>
            {title}
            <SessionStatusIcon status={status} />
          </TitleAndStatusContainer>
          <TitleAndStatusContainer>
            <MDSIconButton iconKind={MdsIconKind.Exit} onClick={handleDismissAndCancel} />
          </TitleAndStatusContainer>
        </SessionTitle>

        {!isCollapsed && (
          <>
            <SessionBody>
              <Icon />
              {nextInputRequestValue && canRenderForm(nextInputRequestValue?.form) ? (
                <InputForm
                  inputRequestValue={nextInputRequestValue}
                  dismissSession={handleDismissAndCancel}
                  metadata={nextInputRequestValue.metadata}
                />
              ) : (
                <MessagesContainer>
                  {groupedMessages.map((group, groupIndex) => {
                    const isCollapsed = !!collapsedGroups[groupIndex];
                    if (group.type === "single" && group.event) {
                      return (
                        <div key={group.event.modelData.id}>
                          <SessionEvent event={group.event} />
                        </div>
                      );
                    } else if (
                      group.type === "reasoning_group" &&
                      group.events &&
                      group.events.length > 0
                    ) {
                      return (
                        <ReasoningGroup key={`reasoning-group-${groupIndex}`}>
                          <ReasoningHeader onClick={() => toggleReasoningGroup(groupIndex)}>
                            {isCollapsed ? "Show thoughts" : "Hide thoughts"}
                            <MdsIcon
                              kind={isCollapsed ? MdsIconKind.AngleDown : MdsIconKind.AngleUp}
                            />
                          </ReasoningHeader>
                          {!isCollapsed && (
                            <div>
                              {group.events.map(event => (
                                <FadeInAnimator
                                  key={event.modelData.id}
                                  element={<SessionEvent event={event} />}
                                />
                              ))}
                            </div>
                          )}
                        </ReasoningGroup>
                      );
                    }

                    return null;
                  })}
                </MessagesContainer>
              )}
            </SessionBody>
            {store.debug.debugModeEnabled && (
              <DebugContent
                onClick={() => {
                  navigator.clipboard.writeText(sessionId);
                }}
              >
                <DebugSessionId>
                  <MdsIcon
                    kind={MdsIconKind.Copy}
                    innerStyles={{
                      Icon: { className: css({ color: mdsColors().grey.x300, width: 8 }) },
                    }}
                  />
                  {sessionId}
                </DebugSessionId>
                <DismissLink
                  href="#"
                  onClick={e => {
                    e.stopPropagation();
                    e.preventDefault();
                    handleDismissAndCancel();
                  }}
                >
                  Dismiss
                </DismissLink>
              </DebugContent>
            )}
          </>
        )}
      </Wrapper>
    );
  }
);

Session.displayName = "Session";
